import React, { useEffect, useState } from 'react';
import PrintIcon from '@material-ui/icons/Print';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import jsPDF from 'jspdf';
import PopupMessage from './PopupMessage';
import { RootState } from '../../../../../redux/root';
import { closeInspection, getFieldsForInspection, persistInspectionData } from '../../Utils';
import { Loader, SuccessCheckmark } from '../../../../../atoms';
import { sendInspectionMessage, updatePermitInspection } from '../../../../../api/inspections';
import { setPermitInspectionsList, setShowPopup } from '../../../../../redux/slices/inspectionsSlice';
import { overlayInfo, THRESHOLD_FONTSIZE } from '../../../../../organisms/ThresholdReports/docOvelayTool';
import { buildReportBlob, fetchSourcePdf } from '../../../../../organisms/ThresholdReports/mergeSourcePdf';
import { getSignature } from '../../../../../api/userAuth';

const InspectionSaving = ({ reportPdf, pdfObject }) => {
  const { formData, selectedInspection, pdfReport, permitInspectionsList } = useSelector((s: RootState) => s.inspections);
  const [showSuccess, setShowSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [signature, setSignature] = useState<string>('');
  const userId = useSelector((s: RootState) => s.userAuth.info.id);
  const selectedForm = useSelector((s: RootState) => s.thresholdForms.selectedForm);
  useEffect(() => {
    const load = async () => {
      const payload = getFieldsForInspection(selectedInspection, formData);
      try {
        // The report first. It used to follow the field save, so a failed
        // upload left the result saved with no report behind it.
        if (reportPdf) {
          await persistInspectionData(selectedInspection, reportPdf);
        }
        // Always saved. This used to sit inside `if (reportPdf)`, so a status
        // change with no report -- to Scheduled, say -- showed "Inspection
        // saved" without sending anything.
        const result = await updatePermitInspection({ ...payload });
        const refreshList = permitInspectionsList.reduce((inspArr, item) => {
          if (item.id !== result.data.id) {
            inspArr.push(item);
          }
          return inspArr;
        }, []);
        dispatch(setPermitInspectionsList(refreshList));
      } catch (e) {
        setSaveError(
          reportPdf
            ? 'The inspection was not saved: the report or the inspection could not be sent. Go back and try again.'
            : 'The inspection was not saved. Go back and try again.',
        );
        return;
      }

      // The completion message, only for a result -- which always carries a
      // report now.
      if (reportPdf) {
        sendInspectionMessage({
          address: selectedInspection.projectAddress,
          inspection_id: selectedInspection.id,
          inspection_status: formData.activityStatus.name,
          inspector_id: selectedInspection.inspector,
          phone_number: selectedInspection.cellPhone,
          information: formData.information,
          report_status: 'Attached',
        });
      }
      setShowSuccess(true);
    };
    load();
  }, []);

  useEffect(() => {
    if (userId) {
      const requestSignature = async () => {
        const _signature = await getSignature(userId);
        setSignature(_signature);
      };
      requestSignature();
    }
  }, [userId]);

  const dispatch = useDispatch();

  const saveReport = async (event: any): Promise<void> => {
    if (selectedInspection.formMarkup) {
      const sourceBytes = await fetchSourcePdf(selectedForm?.sourceFile);

      const doc = new jsPDF();
      doc.setFontSize(THRESHOLD_FONTSIZE);
      const { fileBase64 } = selectedForm;
      overlayInfo(doc, fileBase64, selectedInspection.formMarkup, signature, !!sourceBytes);

      // A blob URL, not the old doc.output('dataurlnewwindow'): that builds a
      // base64 data: URI, and browsers refuse those past a few megabytes —
      // exactly the size a page-image background produced.
      const url = URL.createObjectURL(await buildReportBlob(doc, sourceBytes));
      window.open(url, '_blank');
      return;
    }

    if (pdfObject) {
      pdfObject.save(pdfReport);
    }
  };

  let caption = 'Saving inspection';
  if (saveError) caption = 'Inspection not saved';
  else if (showSuccess) caption = 'Inspection saved';

  return (
    <PopupMessage caption={caption}>
      {saveError && <ErrorText>{saveError}</ErrorText>}
      {!showSuccess && !saveError && (
        <StatusRow>
          <Loader />
          <StatusText>Saving your changes…</StatusText>
        </StatusRow>
      )}
      {showSuccess && (
        <>
          <SuccessRow>
            <CheckWrapper>
              <SuccessCheckmark />
            </CheckWrapper>
            <StatusText>All set — the inspection has been saved.</StatusText>
          </SuccessRow>
          {reportPdf && (
            <PrintLink type="button" onClick={saveReport}>
              <PrintIcon />
              <span>Print report</span>
            </PrintLink>
          )}
        </>
      )}
      <Actions>
        <PrimaryButton
          type="button"
          disabled={!showSuccess && !saveError}
          // After a failure, back to the editor with the edits still in it
          // rather than closing and discarding them.
          onClick={() => (saveError ? dispatch(setShowPopup('none')) : closeInspection(dispatch))}
        >
          {saveError ? 'Back to inspection' : 'Close'}
        </PrimaryButton>
      </Actions>
    </PopupMessage>
  );
};

export default InspectionSaving;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const SuccessRow = styled(StatusRow)``;

const ErrorText = styled.div`
  font-size: 0.9375rem;
  color: var(--danger-600);
  text-align: center;
`;

const StatusText = styled.div`
  font-size: 0.9375rem;
  color: var(--neutral-700);
`;

const CheckWrapper = styled.div`
  zoom: 0.4;
  display: inline-flex;
`;

const PrintLink = styled.button`
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: var(--primary-700);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover { background: var(--primary-50); }

  svg { width: 18px; height: 18px; }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
`;

const PrimaryButton = styled.button`
  min-width: 140px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-family: inherit;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) { background: var(--primary-700); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &:focus-visible { outline: none; box-shadow: var(--shadow-focus); }
`;
